// modules/support/fetch/runtime/composables/useApiFetchBlob.ts
import { useCookie, useRuntimeConfig } from '#app';
import { useToaster } from '~/composables/useToaster';

export const useApiFetchBlob = async (
  url: string,
  opts?: Record<string, any> | null | undefined,
) => {
  const config = useRuntimeConfig().public.apiFetch;
  const csrfToken = useCookie(config.csrf.cookieKey).value;
  const { $sanctumAuth, $sentryCaptureException } = useNuxtApp();
  const toaster = useToaster();

  try {
    const response = await fetch(url, {
      method: opts?.method ?? 'GET',
      credentials: 'include',
      headers: {
        ...config.headers,
        [config.csrf.headerKey]: csrfToken,
        ...(opts?.headers ?? {}),
      },
      ...(opts ?? {}),
    });

    if ([401, 419].includes(response.status) && !['/me'].includes(url)) {
      $sanctumAuth.logout();
      toaster.add('danger', 'Session has expired');
      throw new Error('Authentication failed');
    }

    if (response.status === 403) {
      toaster.add('danger', 'This action is unauthorized.');
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      let errorData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        errorData = await response.text();
      }

      $sentryCaptureException({
        status: response.status,
        data: errorData,
        url,
      });

      throw new Error(
        typeof errorData === 'object' && errorData.error
          ? errorData.error
          : 'Download failed',
      );
    }

    return {
      blob: await response.blob(),
      headers: response.headers,
      status: response.status,
    };
  } catch (error) {
    console.error('API fetch blob error:', error);
    $sentryCaptureException(error);
    throw error;
  }
};
